/*
 * A cosmetic MLB team lookup (public brand abbreviations and primary colors) for the
 * Catalogue browse's crests, keyed by the team's full name as it appears in Event names
 * (CONTEXT.md: "Away Team at Home Team"). This is display-only — a stand-in until the real
 * team registry (logos, synced via the metadata sync slice, API-18/API-20) ships.
 *
 * Feed/mock Event titles sometimes use nicknames ("Yankees at Red Sox") rather than the
 * canonical club name; lookup resolves full names, official abbreviations, and unique
 * nickname / suffix forms so Crests keep their brand colors.
 */

import { splitEventTeams } from './format';

export interface TeamLookup {
  readonly abbr: string;
  readonly color?: string;
}

export interface EventCrests {
  readonly home: TeamLookup;
  readonly away: TeamLookup;
}

const MLB_TEAMS: Record<string, TeamLookup> = {
  'Arizona Diamondbacks': { abbr: 'AZ', color: '#a71930' },
  'Atlanta Braves': { abbr: 'ATL', color: '#ce1141' },
  'Baltimore Orioles': { abbr: 'BAL', color: '#df4601' },
  'Boston Red Sox': { abbr: 'BOS', color: '#bd3039' },
  'Chicago Cubs': { abbr: 'CHC', color: '#0e3386' },
  'Chicago White Sox': { abbr: 'CWS', color: '#27251f' },
  'Cincinnati Reds': { abbr: 'CIN', color: '#c6011f' },
  'Cleveland Guardians': { abbr: 'CLE', color: '#00385d' },
  'Colorado Rockies': { abbr: 'COL', color: '#33006f' },
  'Detroit Tigers': { abbr: 'DET', color: '#0c2340' },
  'Houston Astros': { abbr: 'HOU', color: '#002d62' },
  'Kansas City Royals': { abbr: 'KC', color: '#004687' },
  'Los Angeles Angels': { abbr: 'LAA', color: '#ba0021' },
  'Los Angeles Dodgers': { abbr: 'LAD', color: '#005a9c' },
  'Miami Marlins': { abbr: 'MIA', color: '#00a3e0' },
  'Milwaukee Brewers': { abbr: 'MIL', color: '#12284b' },
  'Minnesota Twins': { abbr: 'MIN', color: '#002b5c' },
  'New York Mets': { abbr: 'NYM', color: '#002d72' },
  'New York Yankees': { abbr: 'NYY', color: '#0c2340' },
  'Oakland Athletics': { abbr: 'OAK', color: '#003831' },
  'Philadelphia Phillies': { abbr: 'PHI', color: '#e81828' },
  'Pittsburgh Pirates': { abbr: 'PIT', color: '#fdb827' },
  'San Diego Padres': { abbr: 'SD', color: '#2f241d' },
  'San Francisco Giants': { abbr: 'SF', color: '#fd5a1e' },
  'Seattle Mariners': { abbr: 'SEA', color: '#0c2c56' },
  'St. Louis Cardinals': { abbr: 'STL', color: '#c41e3a' },
  'Tampa Bay Rays': { abbr: 'TB', color: '#092c5c' },
  'Texas Rangers': { abbr: 'TEX', color: '#003278' },
  'Toronto Blue Jays': { abbr: 'TOR', color: '#134a8e' },
  'Washington Nationals': { abbr: 'WSH', color: '#ab0003' },
};

/** Resolve a side string ("Boston Red Sox", "Yankees", "BOS") to its crest lookup. */
export function lookupTeam(name: string): TeamLookup {
  const trimmed = name.trim();
  if (trimmed === '') return { abbr: '?' };

  const exact = MLB_TEAMS[trimmed];
  if (exact) return exact;

  const needle = trimmed.toLowerCase();
  for (const lookup of Object.values(MLB_TEAMS)) {
    if (lookup.abbr.toLowerCase() === needle) return lookup;
  }

  // Unique nickname / suffix: "Yankees" → New York Yankees, "Red Sox" → Boston Red Sox.
  const suffixHits = Object.entries(MLB_TEAMS).filter(([full]) => {
    const lower = full.toLowerCase();
    return lower === needle || lower.endsWith(` ${needle}`);
  });
  if (suffixHits.length === 1) return suffixHits[0]![1];
  if (suffixHits.length > 1) {
    // Prefer the shortest club name when an ambiguous token somehow collides.
    suffixHits.sort((a, b) => a[0].length - b[0].length);
    return suffixHits[0]![1];
  }

  return { abbr: trimmed };
}

/**
 * Crests for an Event row, or null when the name doesn't name two sides. CrestPair paints
 * `home` left then `away` right — pass feed away/home so the left crest matches the first
 * team in the Event name.
 */
export function crestsForEventName(name: string): EventCrests | null {
  const teams = splitEventTeams(name);
  if (!teams) return null;
  return {
    home: lookupTeam(teams.away),
    away: lookupTeam(teams.home),
  };
}
